/**
 * Dashboard Component
 *
 * Alpine.js component for the unified user dashboard.
 * Supports tabs: overview, horses, favorites, subscription, orders, profile, notifications.
 *
 * @package Lovasok_Marketplace
 */

import { LovasokAPI } from '../utils/api.js';
import { formatPrice } from '../utils/formatters.js';
import { isHorseface, successCopy } from '../utils/listing-mode.js';

export default (initialTab = 'overview', config = {}) => ({
    activeTab: initialTab,
    horses: [],
    favoritesList: [],
    notifications: [],
    loading: true,
    initialLoading: true,
    loadingFavorites: false,
    savingProfile: false,
    savingBilling: false,
    showHighlightModal: false,
    isAddingHighlight: false,
    selectedHorseForHighlight: null,
    selectedHighlightVariationId: null,
    highlightProductId: Number(config.highlightProductId) || 0,
    highlightOptions: Array.isArray(config.highlightOptions) ? config.highlightOptions : [],
    freeCredits: config.freeCredits || { normal: 0, premium: 0 },
	packageHighlightSlots: Array.isArray(config.packageHighlightSlots) ? config.packageHighlightSlots : [],

    // Sikeres feladás popup (eladó hirdetés vagy — ritkán — élő profil)
    showListingSuccessModal: false,
    listingSuccessId: null,
    listingSuccessMode: 'elado',
    stats: {
        views: 0,
        favorites: 0,
        messages: 0,
        activeAds: 0
    },
    passwordForm: {
        current: '',
        new: '',
        confirm: ''
    },

    // WooCommerce Subscriptions (REST)
    subscriptions: [],
    loadingSubscriptions: false,
    highlightSubId: Number(config.highlightSubId) || 0,
    showSubActionModal: false,
    subActionType: '',
    subActionTargetId: null,
    subActionTitle: '',
    subActionBody: '',
    subActionLoading: false,
    subActionFeedback: '',
    subActionFeedbackType: '',

    tabs: [
        {
            id: 'overview',
            label: 'Áttekintés',
            icon: '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"/></svg>',
            badge: null
        },
        {
            id: 'horses',
            label: 'Lovaim',
            icon: '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"/></svg>',
            badge: null
        },
        {
            id: 'reminders',
            label: 'Emlékeztetők',
            icon: '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>',
            badge: null
        },
        {
            id: 'favorites',
            label: 'Kedvencek',
            icon: '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"/></svg>',
            badge: null
        },
        {
            id: 'subscription',
            label: 'Előfizetés',
            icon: '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"/></svg>',
            badge: null
        },
        {
            id: 'orders',
            label: 'Rendelések',
            icon: '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"/></svg>',
            badge: null
        },
        {
            id: 'profile',
            label: 'Profil',
            icon: '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/></svg>',
            badge: null
        },
        {
            id: 'notifications',
            label: 'Értesítések',
            icon: '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"/></svg>',
            badge: null
        }
    ],

    async init() {
        this.consumeListingCreatedFlag();

        // Legacy URL: ?tab=stable → Lovaim (táblázat nézet)
        try {
            const params = new URLSearchParams(window.location.search);
            if (params.get('tab') === 'stable' || this.activeTab === 'stable') {
                this.activeTab = 'horses';
                params.set('tab', 'horses');
                history.replaceState({}, '', window.location.pathname + '?' + params.toString());
            }
        } catch {
            // ignore
        }

        try {
            await Promise.all([
                this.fetchHorses(),
                this.fetchFavorites(),
                this.fetchNotifications(),
                this.fetchStats(),
                this.fetchSubscriptions()
            ]);
        } finally {
            this.initialLoading = false;
            this.$nextTick(() => document.getElementById('lmk-dashboard-preload')?.remove());
        }

        const unreadCount = this.notifications.filter(n => !n.read).length;
        if (unreadCount > 0) {
            const notifTab = this.tabs.find(t => t.id === 'notifications');
            if (notifTab) notifTab.badge = unreadCount;
        }

        const horsesTab = this.tabs.find(t => t.id === 'horses');
        if (horsesTab) {
            const saleCount = this.horses.filter(h => h.listingType !== 'horseface').length;
            const hfCount = this.horses.filter(h => h.listingType === 'horseface').length;
            horsesTab.badge = this.horses.length || null;
            horsesTab.saleCount = saleCount;
            horsesTab.hfCount = hfCount;
        }

    },

    consumeListingCreatedFlag() {
        try {
            const params = new URLSearchParams(window.location.search);
            if (params.get('listing_created') !== '1') {
                return;
            }

            const listingId = parseInt(params.get('listing_id') || '', 10);
            this.listingSuccessId = Number.isFinite(listingId) && listingId > 0 ? listingId : null;
            const modeParam = (params.get('listing_mode') || '').toLowerCase();
            this.listingSuccessMode = isHorseface(modeParam) ? 'horseface' : 'elado';
            this.showListingSuccessModal = true;
            this.activeTab = 'horses';

            params.delete('listing_created');
            params.delete('listing_id');
            params.delete('listing_mode');
            if (!params.has('tab')) {
                params.set('tab', 'horses');
            }
            const qs = params.toString();
            history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''));
        } catch {
            // ignore URL parse errors
        }
    },

    closeListingSuccessModal() {
        this.showListingSuccessModal = false;
    },

    getListingSuccessHorse() {
        if (!this.listingSuccessId || !Array.isArray(this.horses)) {
            return null;
        }
        return this.horses.find(h => Number(h.id) === Number(this.listingSuccessId)) || null;
    },

    getListingSuccessCopy() {
        const horse = this.getListingSuccessHorse();
        const mode = horse?.listingType || this.listingSuccessMode || 'elado';
        return successCopy(mode, horse);
    },

    setTab(tabId) {
        if (tabId === 'stable') {
            tabId = 'horses';
        }

        const tab = this.tabs.find(t => t.id === tabId);
        if (tab && tab.isLink && tab.url) {
            window.location.href = tab.url;
            return;
        }

        this.activeTab = tabId;

        const base = lovasokParams.myAccountUrl || lovasokParams.homeUrl + '/fiokom';
        try {
            const url = new URL(base, window.location.origin);
            if (tabId === 'overview') {
                url.searchParams.delete('tab');
                url.searchParams.delete('sub');
            } else {
                url.searchParams.set('tab', tabId);
            }
            history.replaceState({}, '', url.pathname + url.search);
        } catch {
            // Fallback for older browsers
            const newUrl = tabId === 'overview' ? base : `${base}?tab=${encodeURIComponent(tabId)}`;
            history.replaceState({}, '', newUrl);
        }
    },

    async fetchHorses() {
        this.loading = true;
        try {
            const data = await LovasokAPI.get('horses', {
                author: 'me',
                status: 'any',
                per_page: 100
            });
            
            // Mindig aktívak elől
            this.horses = (data.horses || []).sort((a, b) => {
                if (a.status === 'publish' && b.status !== 'publish') return -1;
                if (a.status !== 'publish' && b.status === 'publish') return 1;
                return 0;
            });
            
            this.calculateStats();
        } catch (error) {
            this.horses = [];
        } finally {
            this.loading = false;
        }
    },

    async fetchFavorites() {
        this.loadingFavorites = true;
        try {
            const data = await LovasokAPI.get('horses', {
                favorites: true,
                per_page: 50
            });
            this.favoritesList = data.horses || [];
        } catch {
            this.favoritesList = [];
        } finally {
            this.loadingFavorites = false;
        }
    },

    async removeFavorite(horseId) {
        try {
            await LovasokAPI.post(`horses/${horseId}/favorite`);
            this.favoritesList = this.favoritesList.filter(h => h.id !== horseId);
            this.showToast('Eltávolítva a kedvencekből.', 'success');
        } catch {
            this.showToast('Hiba történt.', 'error');
        }
    },

    async fetchNotifications() {
        let notifs = [];

        try {
            const [remindersData, notifData] = await Promise.all([
                LovasokAPI.get('reminders'),
                LovasokAPI.get('notifications').catch(() => null)
            ]);
            
            const getTypeLabel = (type) => {
                return {
                    vaccination: 'Oltás',
                    farrier: 'Patkolás',
                    vet_checkup: 'Állatorvos',
                    deworming: 'Féreghajtás',
                    dental: 'Fogászat',
                    competition: 'Verseny',
                    training: 'Edzés',
                    other: 'Egyéb'
                }[type] || type;
            };

            const getNotifTypeLabel = (type) => ({
                message: 'Üzenet',
                transfer: 'Tulajdonosváltás',
                highlight: 'Kiemelés',
                reminder: 'Emlékeztető',
                system: 'Rendszerértesítés',
            }[type] || type);

            const getNotifDefaultText = (type) => ({
                message: 'Új üzenet érkezett',
                transfer: 'Tulajdonosváltási értesítés',
                highlight: 'Kiemelés értesítés',
                reminder: 'Emlékeztető',
                system: 'Fontos rendszerüzenet',
            }[type] || 'Értesítés');

            const reminderNotifs = (Array.isArray(remindersData) ? remindersData : (remindersData?.reminders || [])).map(rem => ({
                id: `rem_${rem.id}`,
                real_id: rem.id,
                text: `${getTypeLabel(rem.type)} esedékes: ${rem.horse_name || 'Ismeretlen ló'} (${rem.notes || ''})`,
                time: rem.due_date,
                read: rem.is_completed == 1,
                isReminder: true
            })).filter(r => !r.read);

            const realNotifs = (notifData && Array.isArray(notifData.notifications) ? notifData.notifications : []).map(n => {
                const body = (n.text || n.message || '').trim();
                return {
                    id: n.id,
                    real_id: n.id,
                    type: n.type || '',
                    typeLabel: getNotifTypeLabel(n.type),
                    text: body || getNotifDefaultText(n.type),
                    time: n.time || n.created_at,
                    read: n.read === true || n.read === 1 || n.is_read == 1,
                    isReminder: false
                };
            });

            notifs = [...reminderNotifs, ...realNotifs].sort((a, b) => new Date(b.time) - new Date(a.time));
        } catch {
            // silently fail
        }

        this.notifications = notifs;
    },

    openHighlightModal(horse) {
        if (!horse || !horse.id) return;
        if (horse.highlightType && horse.highlightType !== 'none') {
			const usesPackageSlot = this.packageHighlightSlots.some(slot => Number(slot.listingId) === Number(horse.id));
			this.showToast(
				usesPackageSlot
					? 'Az áthelyezéshez annál a hirdetésnél válaszd a Kiemelés műveletet, amelyikre át szeretnéd tenni.'
					: 'Ez a hirdetés már rendelkezik aktív kiemeléssel.',
				'error'
			);
            return;
        }
        this.selectedHorseForHighlight = horse;
		this.selectedHighlightVariationId = this.defaultHighlightSelection(horse.id);
        this.showHighlightModal = true;
    },

	defaultHighlightSelection(horseId) {
		if (Number(this.freeCredits.premium) > 0) return 'free_premium';
		if (Number(this.freeCredits.normal) > 0) return 'free_normal';
		const movableSlot = this.packageHighlightSlots.find(slot => Number(slot.listingId) !== Number(horseId));
		if (movableSlot) return `move_${movableSlot.type}_${movableSlot.listingId}`;
		return this.highlightOptions[0]?.variationId || null;
	},

	isPackageHighlightSelection(value) {
		return typeof value === 'string' && (value.startsWith('free_') || value.startsWith('move_'));
	},

	hasPackageHighlightChoices() {
		return Number(this.freeCredits.premium) > 0
			|| Number(this.freeCredits.normal) > 0
			|| this.packageHighlightSlots.length > 0;
	},

    closeHighlightModal() {
        this.showHighlightModal = false;
        this.isAddingHighlight = false;
        this.selectedHorseForHighlight = null;
        this.selectedHighlightVariationId = null;
    },

    async confirmHighlightPurchase(replaceConfirmed = false) {
        const horseId = this.selectedHorseForHighlight?.id;
        const variationId = this.selectedHighlightVariationId;

        if (!horseId || !variationId) {
            this.showToast('Válassz kiemelési opciót.', 'error');
            return;
        }

        this.isAddingHighlight = true;

		if (this.isPackageHighlightSelection(variationId)) {
            try {
				const moveMatch = /^move_(normal|premium)_(\d+)$/.exec(variationId);
				const highlightType = moveMatch ? moveMatch[1] : (variationId === 'free_premium' ? 'premium' : 'normal');
				const payload = { type: highlightType };
				if (moveMatch) payload.from_listing_id = Number(moveMatch[2]);
				const response = await LovasokAPI.post(`horses/${horseId}/apply-free-highlight`, payload);
                
                if (response.success) {
					this.showToast(response.message || 'Sikeres kiemelés! Az oldal újratöltődik...', 'success');
                    setTimeout(() => window.location.reload(), 1500);
                } else {
                    this.showToast(response.message || 'Nem sikerült aktiválni a kiemelést.', 'error');
                    this.isAddingHighlight = false;
                }
            } catch (err) {
                this.showToast(err?.message || 'Hiba történt a kiemelés aktiválásakor.', 'error');
                this.isAddingHighlight = false;
            }
            return;
        }

        const numericVariationId = Number(variationId);
        const selectedOption = this.highlightOptions.find(item => String(item.variationId) === String(variationId));
        const productId = Number(selectedOption?.productId) || this.highlightProductId;
        if (!numericVariationId || !productId) {
            this.showToast('Érvénytelen kiemelési opció.', 'error');
            this.isAddingHighlight = false;
            return;
        }

        try {
            const formData = new FormData();
            formData.append('action', 'lovasok_add_to_cart');
            formData.append('nonce', lovasokParams.cartNonce);
            formData.append('product_id', String(productId));
            formData.append('variation_id', String(variationId));
            formData.append('listing_id', String(horseId));
            formData.append('quantity', '1');
            if (replaceConfirmed) {
                formData.append('replace_confirmed', '1');
            }

            const response = await fetch(lovasokParams.ajaxUrl, {
                method: 'POST',
                body: formData
            });
            const result = await response.json();

            if (!result.success) {
                this.showToast(result.data?.message || 'Nem sikerült kosárba tenni a kiemelést.', 'error');
                this.isAddingHighlight = false;
                return;
            }

            const d = result.data || {};

            if (d.needs_replace_confirm) {
                const existing = d.existing_line_name || '—';
                const next = d.new_line_name || '—';
                const ok = window.confirm(
                    `Már van a kosárban kiemelés ehhez a hirdetéshez:\n„${existing}”\n\nLecseréled erre?\n„${next}”\n\nA korábbi sor törlődik a kosárból.`
                );
                if (!ok) {
                    this.isAddingHighlight = false;
                    return;
                }
                return this.confirmHighlightPurchase(true);
            }

            if (d.already_in_cart) {
                this.isAddingHighlight = false;
                const goToCart = window.confirm(
                    (d.message || 'Ez a kiemelés már a kosárban van ehhez a hirdetéshez.')
                    + '\n\nSzeretnéd megtekinteni a kosarad?'
                );
                if (goToCart && d.cart_url) {
                    window.location.href = d.cart_url;
                }
                return;
            }

            this.closeHighlightModal();

            if (d.cart_url) {
                window.location.href = d.cart_url;
                return;
            }

            this.showToast(
                d.replaced_existing
                    ? (d.message || 'A korábbi kiemelés lecserélve az új választásra.')
                    : 'A kiemelés a kosárba került.',
                'success'
            );
        } catch {
            this.showToast('Hiba történt a kiemelés kosárba helyezésekor.', 'error');
            this.isAddingHighlight = false;
        }
    },

    async saveProfile() {
        this.savingProfile = true;
        try {
            const form = document.getElementById('lovasok-profile-form');
            const formData = new FormData(form);

            const response = await fetch(lovasokParams.ajaxUrl, {
                method: 'POST',
                body: (() => {
                    const d = new FormData();
                    d.append('action', 'lovasok_save_profile');
                    d.append('nonce', lovasokParams.ajaxNonce);
                    for (const [key, value] of formData.entries()) {
                        d.append(key, value);
                    }
                    return d;
                })()
            });

            const result = await response.json();
            if (result.success) {
                this.showToast('Profil sikeresen mentve!', 'success');
            } else {
                this.showToast(result.data?.message || 'Hiba történt a mentés során.', 'error');
            }
        } catch {
            this.showToast('Hiba történt a mentés során.', 'error');
        } finally {
            this.savingProfile = false;
        }
    },

    async saveBilling() {
        const form = document.getElementById('lovasok-billing-form');
        if (!form) {
            return;
        }

        this.savingBilling = true;
        try {
            const formData = new FormData(form);
            const response = await fetch(lovasokParams.ajaxUrl, {
                method: 'POST',
                body: (() => {
                    const d = new FormData();
                    d.append('action', 'lovasok_save_billing');
                    d.append('nonce', lovasokParams.ajaxNonce);
                    for (const [key, value] of formData.entries()) {
                        d.append(key, value);
                    }
                    return d;
                })()
            });

            const result = await response.json();
            if (result.success) {
                this.showToast(result.data?.message || 'Számlázási adatok mentve.', 'success');
            } else {
                this.showToast(result.data?.message || 'Hiba történt a mentés során.', 'error');
            }
        } catch {
            this.showToast('Hiba történt a mentés során.', 'error');
        } finally {
            this.savingBilling = false;
        }
    },

    async changePassword() {
        if (!this.passwordForm.current || !this.passwordForm.new) {
            this.showToast('Kérjük töltsd ki az összes mezőt.', 'error');
            return;
        }

        if (this.passwordForm.new !== this.passwordForm.confirm) {
            this.showToast('A két jelszó nem egyezik.', 'error');
            return;
        }

        if (this.passwordForm.new.length < 8) {
            this.showToast('A jelszónak legalább 8 karakter hosszúnak kell lennie.', 'error');
            return;
        }

        try {
            const formData = new FormData();
            formData.append('action', 'lovasok_change_password');
            formData.append('nonce', lovasokParams.ajaxNonce);
            formData.append('current_password', this.passwordForm.current);
            formData.append('new_password', this.passwordForm.new);

            const response = await fetch(lovasokParams.ajaxUrl, { method: 'POST', body: formData });
            const result = await response.json();

            if (result.success) {
                this.showToast('Jelszó sikeresen módosítva!', 'success');
                this.passwordForm = { current: '', new: '', confirm: '' };
            } else {
                this.showToast(result.data?.message || 'Hiba történt.', 'error');
            }
        } catch {
            this.showToast('Hiba történt a jelszó módosítása során.', 'error');
        }
    },

    updateNotificationBadge() {
        const unreadCount = this.notifications.filter(n => !n.read).length;
        const notifTab = this.tabs.find(t => t.id === 'notifications');
        if (notifTab) {
            notifTab.badge = unreadCount > 0 ? unreadCount : null;
        }
    },

    async markAllRead() {
        try {
            await LovasokAPI.post('notifications/mark-all-read');

            const reminderNotifs = this.notifications.filter(n => n.isReminder && !n.read);
            for (const notif of reminderNotifs) {
                await LovasokAPI.post(`reminders/${notif.real_id}`, { is_completed: true });
            }

            this.notifications = this.notifications.map(n => ({ ...n, read: true }));
            this.updateNotificationBadge();
            this.showToast('Összes értesítés olvasottnak jelölve.', 'success');
        } catch {
            this.showToast('Nem sikerült olvasottnak jelölni az értesítéseket.', 'error');
        }
    },

    async handleNotifClick(notif) {
        if (notif.isReminder) {
            if (confirm('Szeretnéd ezt az emlékeztetőt elvégezettként megjelölni (törölni az értesítések közül)?')) {
                try {
                    await LovasokAPI.post(`reminders/${notif.real_id}`, { is_completed: true });
                    this.notifications = this.notifications.filter(n => n.id !== notif.id);
                    this.updateNotificationBadge();
                } catch {
                    this.showToast('Nem sikerült az emlékeztető lezárása.', 'error');
                }
            } else {
                this.setTab('reminders');
            }
        } else if (!notif.read) {
            try {
                await LovasokAPI.post(`notifications/${notif.real_id}/read`);
                notif.read = true;
                this.updateNotificationBadge();
            } catch {
                this.showToast('Nem sikerült olvasottnak jelölni.', 'error');
            }
        }
    },

    showToast(message, type = 'info') {
        const toast = document.createElement('div');
        toast.className = `fixed bottom-4 right-4 z-50 px-6 py-3 rounded-lg shadow-lg text-white text-sm font-medium transition-all transform translate-y-2 opacity-0 ${
            type === 'success' ? 'bg-green-500' : type === 'error' ? 'bg-red-500' : 'bg-primary'
        }`;
        toast.textContent = message;
        document.body.appendChild(toast);
        requestAnimationFrame(() => {
            toast.classList.remove('translate-y-2', 'opacity-0');
        });
        setTimeout(() => {
            toast.classList.add('translate-y-2', 'opacity-0');
            setTimeout(() => toast.remove(), 300);
        }, 3000);
    },

    async fetchStats() {
        try {
            const data = await LovasokAPI.get('stats/my-listings', { days: 30 });
            this.stats.views = data.total_views || 0;
            this.stats.favorites = data.total_favorites || 0;
            this.stats.messages = data.total_messages || 0;

            if (Array.isArray(data.per_listing)) {
                this._statsMap = {};
                data.per_listing.forEach(s => {
                    this._statsMap[s.listing_id] = s;
                });
                this.horses.forEach(h => {
                    const s = this._statsMap[h.id];
                    if (s) {
                        h.views = s.views;
                        h.favorites = s.favorites;
                        h.messages = s.messages;
                    }
                });
            }
        } catch {
            this.calculateStatsFromHorses();
        }
    },

    calculateStats() {
        this.stats.activeAds = this.horses.filter(
            h => h.status === 'publish' && h.saleStatus !== 'sold' && h.saleStatus !== 'closed' && h.listingType !== 'horseface'
        ).length;
    },

    calculateStatsFromHorses() {
        this.stats.views = this.horses.reduce((sum, h) => sum + (parseInt(h.views) || 0), 0);
        this.stats.favorites = this.horses.reduce((sum, h) => sum + (parseInt(h.favorites) || 0), 0);
        this.stats.messages = this.horses.reduce((sum, h) => sum + (parseInt(h.messages) || 0), 0);
        this.stats.activeAds = this.horses.filter(
            h => h.status === 'publish' && h.saleStatus !== 'sold' && h.saleStatus !== 'closed' && h.listingType !== 'horseface'
        ).length;
    },

    formatPrice(price, priceStatus = '', priceCategory = '', currency = 'Ft') {
        return formatPrice(price, true, priceStatus, priceCategory, currency);
    },

    async fetchSubscriptions() {
        this.loadingSubscriptions = true;
        try {
            const data = await LovasokAPI.get('subscriptions');
            this.subscriptions = Array.isArray(data?.subscriptions) ? data.subscriptions : [];
        } catch {
            this.subscriptions = [];
        } finally {
            this.loadingSubscriptions = false;
        }
    },

    getSubStatusDotClass(status) {
        if (status === 'active') return 'bg-green-500';
        if (status === 'on-hold') return 'bg-yellow-400';
        return 'bg-gray-400';
    },

    getSubActionBtnClass(action) {
        if (action === 'cancel') {
            return 'border-red-200 text-red-700 bg-red-50 hover:bg-red-100';
        }
        if (action === 'reactivate') {
            return 'border-green-200 text-green-700 bg-green-50 hover:bg-green-100';
        }
        return 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50';
    },

    openSubActionModal(actionType, subscriptionId) {
        const configs = {
            cancel: {
                title: 'Előfizetés lemondása',
                message: 'Biztosan le akarod mondani az előfizetésedet? A jelenlegi időszak végéig továbbra is használhatod a csomagot.',
            },
            reactivate: {
                title: 'Előfizetés újraaktiválása',
                message: 'Biztosan újra szeretnéd aktiválni az előfizetésedet? A következő díjat az eredeti ütemezés szerint számítjuk fel.',
            },
        };
        const config = configs[actionType];
        if (!config) return;

        this.subActionType = actionType;
        this.subActionTargetId = subscriptionId;
        this.subActionTitle = config.title;
        this.subActionBody = config.message;
        this.showSubActionModal = true;
    },

    closeSubActionModal() {
        this.showSubActionModal = false;
        this.subActionType = '';
        this.subActionTargetId = null;
        this.subActionTitle = '';
        this.subActionBody = '';
    },

    async confirmSubAction() {
        if (!this.subActionTargetId || !this.subActionType || this.subActionLoading) return;

        this.subActionLoading = true;
        try {
            const data = await LovasokAPI.post(
                `subscriptions/${this.subActionTargetId}/${this.subActionType}`,
                {}
            );
            this.subActionFeedback = data?.message || 'Sikeres művelet.';
            this.subActionFeedbackType = 'success';
            this.closeSubActionModal();
            await this.fetchSubscriptions();
        } catch (error) {
            this.subActionFeedback = error?.message || 'Hiba történt a művelet során.';
            this.subActionFeedbackType = 'error';
            this.closeSubActionModal();
        } finally {
            this.subActionLoading = false;
        }
    },

});
